import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Slider,
  Tooltip,
} from '@mui/material';

const RadioSelect =
  'https://assets.askhippo.co.za/Icons/common/radio-select.svg';
const RadioDeselect =
  'https://assets.askhippo.co.za/Icons/common/radio-deselect.svg';

// ---------- helpers ----------
const isSection = (v) =>
  v &&
  typeof v === 'object' &&
  typeof v.type === 'string' &&
  ['radio', 'checkbox', 'slider'].includes(v.type);

const getSections = (config) =>
  Object.entries(config).filter(([, v]) => isSection(v));

const readTooltip = (obj) => obj?.tooltip ?? obj?.tooltip ?? '';

const getDefaultFor = (obj) => {
  if (obj.type === 'radio') {
    return obj.defaultSelectedOption ?? obj.values?.[0]?.value ?? '';
  }
  if (obj.type === 'checkbox') {
    return Array.isArray(obj.defaultSelectedOptions)
      ? obj.defaultSelectedOptions
      : [];
  }
  if (obj.type === 'slider') {
    return Number.isFinite(obj.defaultValue)
      ? obj.defaultValue
      : (obj.start ?? 0);
  }
  return null;
};

const getCurrent = (config, key, obj) => {
  const map = config.selectedSections || {};
  if (map[key] !== undefined) return map[key];
  return getDefaultFor(obj);
};

const setCurrent = (config, setConfig, key, value) => {
  setConfig({
    ...config,
    selectedSections: {
      ...(config.selectedSections || {}),
      [key]: value,
    },
  });
};

const buildDefaultsMap = (config) => {
  const out = {};
  for (const [key, obj] of getSections(config)) {
    out[key] = getDefaultFor(obj);
  }
  return out;
};

// style bits
const InfoWithTooltip = ({ message }) => (
  <Tooltip
    title={<span>{message}</span>}
    placement="right"
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.002em',
          borderRadius: '12px',
          border: '2px solid #E3E800',
          backgroundColor: '#FFFFFF',
          p: '16px',
          boxShadow: '0px 3px 8px #162F3B1F, 0px 1px 2px #A2ACB133',
          maxWidth: '297px',
          color: '#000',
          '& .MuiTooltip-arrow': { color: '#E3E800' },
        },
      },
      arrow: { sx: { color: '#E3E800' } },
    }}
  >
    <img
      src={InfoIcon}
      alt="Info"
      style={{ width: 16, height: 16, cursor: 'pointer' }}
    />
  </Tooltip>
);

const UncheckedRadioIcon = () => (
  <img
    src={RadioDeselect}
    alt="Unchecked"
    style={{ width: 15, height: 15, display: 'block' }}
  />
);
const CheckedRadioIcon = () => (
  <img
    src={RadioSelect}
    alt="Checked"
    style={{ width: 15, height: 15, display: 'block' }}
  />
);

const InfoIcon = 'https://assets.askhippo.co.za/Icons/common/info.svg';

// ---------- component ----------
const FilterPanelComponent = ({
  config,
  setConfig,
  filterPanelHeader,
  filterPanelSubHeader,
  onApply,
  onReset,
  onClose,
  initialLoadDone = true,
}) => {
  const sections = getSections(config);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const handleReset = () => {
    setConfig({
      ...config,
      selectedSections: buildDefaultsMap(config),
    });
    onReset?.();
  };

  const handleApply = () => {
    onApply?.(config);
    onClose?.();
  };

  return (
    <Box
      sx={{
        width: 195,
        borderRadius: '22px',
        backgroundColor: '#fff',
        border: '1px solid #E0E0E0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        ml: '32px',
        pointerEvents: initialLoadDone ? 'auto' : 'none',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#EDEDEE',
          px: '19px',
          pt: '15px',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: '0.875rem',
            lineHeight: '24px',
            color: '#1C2F3A',
          }}
        >
          {filterPanelHeader}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: '0.813rem',
            lineHeight: '24px',
            color: '#4F4F4F',
            mt: 0,
            mb: '16px',
          }}
        >
          {filterPanelSubHeader}
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{ px: 2, pt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        {sections.map(([key, obj], idx) => {
          const tooltip = readTooltip(obj);

          // divider above every section except the first
          const showDivider = idx > 0;

          return (
            <Box key={key}>
              {showDivider && (
                <Box
                  sx={{
                    width: 'calc(100% + 32px)',
                    height: '1px',
                    backgroundColor: '#E0E0E0',
                    mt: 1.5,
                    mb: 1.5,
                    ml: '-16px',
                  }}
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '0.813rem',
                    lineHeight: '28px',
                    color: '#1C2F3A',
                    mb: 0,
                  }}
                >
                  {obj.header ?? key}
                </Typography>
                {tooltip ? (
                  <InfoWithTooltip message={tooltip} />
                ) : (
                  <span style={{ width: 16, height: 16 }} />
                )}
              </Box>

              {/* RADIO */}
              {obj.type === 'radio' && (
                <RadioGroup
                  value={getCurrent(config, key, obj)}
                  onChange={(e) =>
                    setCurrent(config, setConfig, key, e.target.value)
                  }
                >
                  {obj.values.map(({ label, value }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      sx={{
                        alignItems: 'center',
                        mb: '0px',
                        ml: 0,
                        mr: 0,
                        opacity: initialLoadDone ? 1 : 0.5,
                      }}
                      control={
                        <Radio
                          icon={<UncheckedRadioIcon />}
                          checkedIcon={<CheckedRadioIcon />}
                          sx={{ p: 0, mr: '11px' }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: '0.688rem',
                            lineHeight: '22px',
                            color: '#1E354E',
                          }}
                        >
                          {label}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              )}

              {/* CHECKBOX (multi-select) */}
              {obj.type === 'checkbox' &&
                (() => {
                  const allValues = obj.values || [];
                  const collapseAt = obj.collapsedCount ?? 3;
                  const collapsible = allValues.length > collapseAt;
                  const isExpanded = expandedSections.has(key);
                  const visibleValues =
                    collapsible && !isExpanded
                      ? allValues.slice(0, collapseAt)
                      : allValues;

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        opacity: initialLoadDone ? 1 : 0.5,
                      }}
                    >
                      {visibleValues.map(({ label, value }) => {
                        const selected = new Set(getCurrent(config, key, obj));
                        const checked = selected.has(value);
                        const toggle = () => {
                          if (checked) selected.delete(value);
                          else selected.add(value);
                          setCurrent(
                            config,
                            setConfig,
                            key,
                            Array.from(selected)
                          );
                        };
                        return (
                          <FormControlLabel
                            key={value}
                            sx={{ alignItems: 'center', ml: 0, mr: 0 }}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={toggle}
                                icon={
                                  <Box
                                    sx={{
                                      width: 15,
                                      height: 15,
                                      borderRadius: '4px',
                                      backgroundColor: '#FFFFFF',
                                      border: '1px solid #D3D4D5',
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <Box
                                    sx={{
                                      width: 15,
                                      height: 15,
                                      borderRadius: '4px',
                                      backgroundColor: '#C9CC37',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#1C2F3A"
                                      strokeWidth="3.25"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      shapeRendering="geometricPrecision"
                                    >
                                      <polyline points="5 13 10 18 20 7" />
                                    </svg>
                                  </Box>
                                }
                                sx={{ p: '1px' }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontFamily: 'Inter',
                                  fontWeight: 500,
                                  fontSize: '0.688rem',
                                  lineHeight: '22px',
                                  color: '#1E354E',
                                  pl: '11px',
                                }}
                              >
                                {label}
                              </Typography>
                            }
                          />
                        );
                      })}

                      {collapsible && (
                        <Typography
                          onClick={() =>
                            setExpandedSections((prev) => {
                              const next = new Set(prev);
                              if (isExpanded) next.delete(key);
                              else next.add(key);
                              return next;
                            })
                          }
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            lineHeight: '20px',
                            letterSpacing: '-0.002em',
                            color: '#D0D5D8',
                            verticalAlign: 'middle',
                            cursor: 'pointer',
                            mt: '2px',
                            '&:hover': { opacity: 0.8 },
                          }}
                        >
                          {isExpanded ? 'View less -' : `View more +`}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

              {/* SLIDER */}
              {obj.type === 'slider' &&
                (() => {
                  const sliderValue = getCurrent(config, key, obj);
                  const isSingleValue = !Array.isArray(sliderValue);
                  const displayAsSingle = obj.displayMode === 'single';
                  return (
                    <Box sx={{ mt: 1.5, opacity: initialLoadDone ? 1 : 0.5 }}>
                      {isSingleValue && displayAsSingle ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 500,
                              fontSize: '0.688rem',
                              lineHeight: '20px',
                              color: '#1C2F3A',
                            }}
                          >
                            {obj.prefix ?? ''}
                            {Number(sliderValue).toLocaleString('en-ZA')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 500,
                              fontSize: '0.688rem',
                              lineHeight: '20px',
                              color: '#1C2F3A',
                            }}
                          >
                            {obj.prefix ?? ''}
                            {Number(obj.start).toLocaleString('en-ZA')}
                          </Typography>
                          <Typography
                            sx={{
                              position: 'absolute',
                              left: '50%',
                              fontFamily: 'Inter',
                              fontWeight: 400,
                              fontSize: '0.6875rem',
                              lineHeight: '1.25rem',
                              letterSpacing: '-0.002em',
                              textAlign: 'right',
                              color: '#162F3B',
                            }}
                          >
                            –
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 500,
                              fontSize: '0.688rem',
                              lineHeight: '22px',
                              color: '#1E354E',
                              ml: 'auto',
                            }}
                          >
                            {obj.prefix ?? ''}
                            {Number(
                              isSingleValue ? sliderValue : sliderValue[1]
                            ).toLocaleString('en-ZA')}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ px: '7.5px' }}>
                        <Slider
                          min={obj.start}
                          max={obj.end}
                          step={obj.step ?? 1}
                          value={sliderValue}
                          onChange={(_, val) =>
                            setCurrent(config, setConfig, key, val)
                          }
                          sx={{
                            color: '#C9CC37',
                            height: 4,
                            '& .MuiSlider-thumb': {
                              width: 15,
                              height: 15,
                              backgroundColor: '#C9CC37',
                              boxShadow: 'none',
                            },
                            '& .MuiSlider-track': {
                              height: 2,
                              border: 'none',
                              ml: '-7.5px',
                              mr: '7.5px',
                            },
                            '& .MuiSlider-rail': {
                              height: 2,
                              backgroundColor:
                                isSingleValue && displayAsSingle
                                  ? '#C9CC37'
                                  : '#E0E0E0',
                              ml: '7.5px',
                              mr: '-7.5px',
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })()}
            </Box>
          );
        })}

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2.3,
          }}
        >
          <Typography
            onClick={handleReset}
            sx={{
              mt: '16px',
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: '1.25rem',
              letterSpacing: '-0.002em',
              color: '#A2ACB1',
              cursor: 'pointer',
              px: '8px',
              py: '4px',
              borderRadius: '8px',
              pointerEvents: initialLoadDone ? 'auto' : 'none',
              opacity: initialLoadDone ? 1 : 0.5,
              '&:hover': { opacity: 0.9 },
            }}
          >
            Reset
          </Typography>

          <Typography
            onClick={handleApply}
            sx={{
              mt: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              px: '12px',
              py: '6px',
              borderRadius: '12px',
              backgroundColor: '#F0F4F8',
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: '1.25rem',
              letterSpacing: '-0.002em',
              color: '#162F3B',
              cursor: 'pointer',
              pointerEvents: initialLoadDone ? 'auto' : 'none',
              opacity: initialLoadDone ? 1 : 0.5,
              '&:hover': { opacity: 0.9 },
            }}
          >
            Apply Filter
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FilterPanelComponent;
